// Access authority comes only from the server gathered source map, never model output.
// Mailbox/calendar access and semantic scope are separate: being in the work
// mailbox does not prove that a message is professional (e.g. school mail).
export function trustedSourceScope(ref, principalId) {
  const access = ref?.provenance?.access
  if (!ref?.provenance?.owner_id || (principalId && ref.provenance.owner_id !== principalId)) return null
  if (!['owner_work_mailbox', 'owner_work_calendar'].includes(access)) return null
  if (typeof ref.family_only !== 'boolean') return null
  return ref.family_only ? 'family' : 'professional'
}
export function trustedItemScope(refs, principalId) {
  if (!refs?.length) return null
  const scopes = refs.map(ref => trustedSourceScope(ref, principalId))
  if (scopes.some(scope => scope === null)) return null
  return scopes.includes('family') ? 'family' : 'professional'
}

// Internal usefulness and external delivery are separate decisions. Model
// classification describes content; it never authorizes a mailbox or egress.
export function itemChannelScope(refs, principalId, modelFamily, topic) {
  const accessAllowed = !!refs?.length && refs.every(ref => {
    const p = ref?.provenance
    // Existing application task context is reader-scoped, not mailbox ownership.
    // It never qualifies for trustedItemScope/external delivery authority.
    if (ref?.kind === 'task' && p?.access === 'existing_task_context') return !!p.reader_id && (!principalId || p.reader_id === principalId)
    // A teammate's work mail read FOR the principal under a configured
    // delegation (api/_lib/janet-delegation.js). In-app only: the owner is
    // never the principal, so trustedItemScope rejects it and it never
    // carries external-delivery authority.
    if (p?.access === 'delegated_work_mailbox') {
      return !!p.owner_id && !!p.delegate_id && p.owner_id !== p.delegate_id && (!principalId || p.delegate_id === principalId)
    }
    return !!p?.owner_id && (!principalId || p.owner_id === principalId) &&
      ['owner_work_mailbox', 'owner_work_calendar'].includes(p.access)
  })
  const trusted = trustedItemScope(refs, principalId)
  const family = refs?.some(ref => ref?.family_only === true) || trusted === 'family' || modelFamily === true || topic === 'family'
    ? true : trusted === 'professional' || modelFamily === false ? false : null
  return {
    accessAllowed,
    family,
    externalFamilyOnly: family === true ? true : trusted === 'professional' && family === false ? false : null,
    // What the item is about, for in-app presentation. Persisted on every
    // source as `internal_scope` so readers can honor the family gate later.
    internal: family === false ? 'professional' : family === true ? 'family' : 'uncertain',
  }
}

// The model's subject-matter classification. Mixed work/family content counts
// as family (it carries family content); uncertain is neither and is held back
// whenever family logistics is off. A legacy boolean is read only when the
// four-way field is absent.
export const SUBJECT_SCOPES = ['professional', 'family', 'mixed', 'uncertain']
export function modelFamilyFromSubject(item) {
  const s = item?.subject_scope
  if (SUBJECT_SCOPES.includes(s)) return s === 'professional' ? false : s === 'uncertain' ? null : true
  return typeof item?.family_only === 'boolean' ? item.family_only : null
}

// What a persisted offer/noticing row is about. Family wins; professional only
// when every source affirmatively says so; anything else (legacy rows, missing
// or malformed classification) is uncertain.
/** The model gave one of the four answers (as opposed to none, or a malformed one). */
export function explicitlyClassified(item) {
  return SUBJECT_SCOPES.includes(item?.subject_scope)
}

export function persistedSubjectScope(row) {
  if (row?.topic === 'family' || row?.family_only === true) return 'family'
  const sources = Array.isArray(row?.sources) ? row.sources : []
  if (!sources.length) return 'uncertain'
  if (sources.some(s => s?.internal_scope === 'family' || s?.family_only === true)) return 'family'
  return sources.every(s => s?.internal_scope === 'professional') ? 'professional' : 'uncertain'
}

// Every in-app reader applies the CURRENT family gate: with family logistics
// off, only rows affirmatively about work are shown — family, mixed and
// uncertain rows persisted earlier stay hidden until it is turned back on.
export function visibleUnderFamilyGate(row, familyEnabled) {
  return familyEnabled || persistedSubjectScope(row) === 'professional'
}
export function filterByFamilyGate(rows, familyEnabled) {
  return (rows || []).filter(r => visibleUnderFamilyGate(r, familyEnabled))
}
